import { useEffect, useRef, useState } from "react";
import { animate } from "framer-motion";

/**
 * Smoothly animates from the previous number to the next one, formatting each
 * intermediate frame with `format`. Used for the momentous final readout so the
 * value visibly *collapses* rather than snapping.
 *
 * Animates in log-space when crossing orders of magnitude so the motion reads
 * naturally for very large or very small numbers.
 */
export default function AnimatedNumber({
  value,
  format,
  duration = 1.1,
  className,
}: {
  value: number;
  format: (n: number) => string;
  duration?: number;
  className?: string;
}) {
  const [display, setDisplay] = useState(() => format(value));
  const prev = useRef(value);

  useEffect(() => {
    const from = prev.current;
    const to = value;
    prev.current = value;

    if (from === to) {
      setDisplay(format(to));
      return;
    }

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion) {
      setDisplay(format(to));
      return;
    }

    // Animate in log-space when both endpoints are positive — keeps the count
    // perceptually smooth across many orders of magnitude.
    const useLog = from > 0 && to > 0;
    const a = useLog ? Math.log(from) : from;
    const b = useLog ? Math.log(to) : to;

    const controls = animate(a, b, {
      duration,
      ease: [0.22, 1, 0.36, 1], // gentle ease-out
      onUpdate: (latest) => {
        const real = useLog ? Math.exp(latest) : latest;
        setDisplay(format(real));
      },
    });
    return () => controls.stop();
  }, [value, format, duration]);

  return <span className={className}>{display}</span>;
}
